import { defineComponent as _defineComponent } from 'vue'
import {closeTradeOrder, fetchTokenPNL, getTraderOrders} from "@/api/endpoints";
import AccountAddress from "@/components/common/AccountAddress.vue";
import TableResponsive from "@/components/common/TableResponsive.vue";
import TokenImage from "@/components/common/TokenImage.vue";
import TokenPortfolio from "@/components/profile/TokenPortfolio.vue";
import SignatureLoader from "@/mixins/SignatureLoader";
import {useAlertStore, useCacheStore, useSettingsStore, useSolanaStore, useWalletStore,} from "@/pinia";
import {
	convertToHumanAmount,
	convertToHumanNumber,
	ETradeOrderMethod,
	formatAmount,
	formatLargePrice,
	formatMedPrice,
	formatPrice,
	getMintColor,
	ITraderOrders,
	SOL_MINT,
	SOL_TOKEN,
	timeAgo,
	TraderOrdersDTO,
	TradesDTO,
	USDC_MINT,
} from "@/shared";
import store from "@/store";
import {web3} from "@project-serum/anchor";
import {createBurnCheckedInstruction, createCloseAccountInstruction, createHarvestWithheldTokensToMintInstruction, getAssociatedTokenAddressSync,} from "@solana/spl-token";
import Vue, {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {ArcElement, Chart as ChartJS, Tooltip} from "chart.js";
import {Doughnut} from "vue-chartjs";
import MultiTabs from "@/components/common/MultiTabs.vue";
import ProfileIndexLoading from "@/components/loading/ProfileIndexLoading.vue";
import router from "@/router";
import AmountEmoji from "@/components/common/AmountEmoji.vue";
import {fetchTraderTrades} from "@/api/endpoints/stats";

// computed

export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileIndexNext',
  setup(__props) {

const getChartData = computed<any>(() => {
	const data: number[] = [];
	const labels: string[] = [];
	const colors: string[] = [];

	getPositionsFiltered.value?.forEach((item, i) => {
		const token = cacheStore.tokenList[item.mint];

		if (!item.mint) return;
		if (item.totalPrice <= 0) return;

		if (i < MAX_CHART_TOKENS) {
			data.push(Math.ceil((item.totalPrice / totalUSD.value) * 100));
			labels.push(token.symbol);
			colors.push(getMintColor(item.mint));
			return;
		}

		if (!labels.includes("Others")) {
			labels.push("Others");
			data.push(Math.ceil((item.totalPrice / totalUSD.value) * 100));
			colors.push("#aaaaaa");
			return;
		}

		data[data.length - 1] =
			data[data.length - 1] +
			Math.ceil((item.totalPrice / totalUSD.value) * 100);
	});

	return {
		labels,
		datasets: [
			{
				backgroundColor: colors,
				data,
			},
		],
	};
});
const currentAmount = computed<number>(() => {
	return convertToHumanAmount({
		amount: solBalance.value,
		decimals: SOL_TOKEN.decimals,
	});
});
const reclaimSOL = computed<number>(() => {
	let amount = 0;
	Object.values(selectedPositions.value).forEach((f: any) => {
		amount += f.account?.lamports;
	});
	return convertToHumanAmount({
		amount,
		decimals: SOL_TOKEN.decimals,
	});
});
const reclaimUSD = computed<number>(() => {
	return reclaimSOL.value * getTokenPrice(SOL_MINT) || 0;
});
const profitLoss = computed<(item: any) => number>(() => (item) => {
	const mint = item.mint;
	const pnl = pnls.value[mint];

	if (!pnl) return 0;
	if (pnl.error) return 0;

	return item.totalPrice - (pnl?.buyValue - pnl?.sellValue);
});
const totalProfitLoss = computed<{ price: number; pct: number }>(() => {
	let total = 0;
	let totalPct = 0;

	getPositionsFiltered.value?.forEach((item) => {
		if (
			item.totalPrice < 1 ||
			item.mint === SOL_MINT ||
			item.mint === USDC_MINT
		) {
			return;
		}

		const pnl = profitLoss.value(item);
		total += pnl;
		totalPct += pnlPct.value(item);
	});

	return {price: total, pct: Number(totalPct.toFixed(2))};
});
const avgEntry = computed<(item: any) => number>(() => (item) => {
	const mint = item.mint;
	const pnl = pnls.value[mint];

	if (!pnl) return 0;
	if (pnl.error) return 0;

	return pnl?.buyValue / (pnl?.buyAmount / Math.pow(10, pnl?.decimals)) || 0;
});
const pnlPct = computed<(item: any) => number>(() => (item) => {
	const mint = item.mint;
	const pnl = pnls.value[mint];
	const price = getTokenPrice(mint);

	if (!pnl) return 0;
	if (!price) return 0;
	if (pnl.error) return 0;

	const currentBalance = item.account.data.parsed.info.tokenAmount.uiAmount;
	const buyVal = currentBalance * avgEntry.value(item);
	const currentVal = currentBalance * price;
	return Number((((currentVal - buyVal) / buyVal) * 100).toFixed(2)) || 0
});

const getPositionsFiltered = computed<any[]>(() => {
	if (!positions.value) return null;
	if (showRugs.value) return positions.value;
	return positions.value.filter((item) => item.totalPrice > 0.01);
});
const getOrders = computed<ITraderOrders | null>(() => {
	if (orders.value === null && inactiveOrders.value === null) {
		return null;
	}

	if (showInactiveOrders.value) {
		return [...(orders.value ?? []), ...(inactiveOrders.value ?? [])];
	}

	return orders.value;
});

// store
const walletStore = useWalletStore();
const cacheStore = useCacheStore();
const solanaStore = useSolanaStore();
const settingsStore = useSettingsStore();
const alertStore = useAlertStore();

// data
const MAX_CHART_TOKENS = window.innerWidth <= 640 ? 5 : 8;
const loading = ref<boolean>(false);
const loadingTable = ref<boolean>(false);
const solBalance = ref<number>(0);
const totalUSD = ref<number>(0);
const positions = ref<any[] | null>(null);
const recentTrades = ref<any[] | null>(null);
const selectedPositions = ref<any>({});
const tokenPrices = ref<any>({});
const showRugs = ref<boolean>(false);
const pnls = ref<{ [i: string]: any }>({});
const orders = ref<ITraderOrders | null>(null);
const inactiveOrders = ref<ITraderOrders | null>(null);
const showInactiveOrders = ref<boolean>(false);
const privacy = ref<boolean>(localStorage.getItem("fluxbeam:privacy-mode") === "1");

const paused = ref<boolean>(false);

const workerTrade = new Worker("/js/trade-worker.js");

// methods
async function mounted() {
	loading.value = true;
	await Promise.all([getActiveOrders(), getBalanceTokens(), getRecentTrades()]);
	loading.value = false;
	initWorker(walletStore?.address.toString());
}

function unmounted() {
	workerTrade.postMessage({method: "terminate"});
	workerTrade.terminate();
}


function initWorker(subject: string) {
	workerTrade.postMessage({method: "init-wallet", data: subject});

	workerTrade.onmessage = ({data}) => {
		processMessages(data.trades);
	}
}

function processMessages(messages) {
	if (messages.length > 0) {
		let newTrades = []
		for (let i = 0; i < messages.length; i++) {
			const tx = messages[i]
			newTrades.push({
				signature: tx.signature,
				amount: Number(tx.inAmount),
				inMint: tx.inMint,
				outMint: tx.outMint,
				platform: tx.platform,
				program: tx.program,
				sell: tx.outMint === SOL_MINT,
				signer: tx.signer,
				timestamp: tx.createdAt
					? new Date(tx.createdAt).getTime()
					: new Date().getTime(),
				value: tx.value,
				price: tx.price,
			})
		}
		newTrades = newTrades.concat(recentTrades.value)
		if (newTrades.length > 30)
			recentTrades.value = newTrades.slice(0, 30)
		else
			recentTrades.value = newTrades
	}
}

async function getBalanceTokens() {
	for (const i in cacheStore.userTokenAccounts) {
		const account = cacheStore.userTokenAccounts[i];

		await cacheStore.cacheToken(account.mint);
	}
}

function getTotalPrice(item: any) {
	if (item.totalPrice < 0.01) return "<$0.01";

	return formatPrice(item.totalPrice || 0, false);
}

function togglePrivacy(): void {
	privacy.value = !privacy.value
	localStorage.setItem("fluxbeam:privacy-mode", privacy.value ? "1" : "0")
}

async function onReclaimTokens(signAndSendTransaction: any) {
	const srcWallet = new web3.PublicKey(walletStore.address);
	const transactions = [];

	let transaction = new web3.Transaction();
	Object.values(selectedPositions.value).forEach((p: any) => {
		const mint = new web3.PublicKey(p.mint);
		const amount = p.account.data.parsed.info.tokenAmount.amount;
		const decimals = p.account.data.parsed.info.tokenAmount.decimals;
		const program = new web3.PublicKey(p.account.owner);

		const srcAta = getAssociatedTokenAddressSync(
			mint,
			srcWallet,
			false,
			program
		);
		transaction.add(
			createBurnCheckedInstruction(
				srcAta,
				mint,
				srcWallet,
				amount,
				decimals,
				[],
				program
			)
		);

		if (
			(p.account.data?.parsed?.info.extensions?.filter(
				(e) => e.extension === "transferFeeAmount"
			)[0]?.state?.withheldAmount || 0) > 0
		)
			transaction.add(
				createHarvestWithheldTokensToMintInstruction(mint, [srcAta])
			);

		transaction.add(
			createCloseAccountInstruction(srcAta, srcWallet, srcWallet, [], program)
		);

		if (transaction.instructions.length > 10) {
			transactions.push(transaction);
			transaction = new web3.Transaction();
		}
	});
	if (transaction.instructions.length > 0) {
		transactions.push(transaction);
	}

	for (let i = 0; i < transactions.length; i++) {
		const txn = transactions[i];
		const unitLimit = 9_000 * Math.ceil(txn.instructions.length / 2);
		const unitPrice = Math.floor(
			solanaStore.getPriorityFees(settingsStore.settings) / unitLimit
		);
		txn.add(
			web3.ComputeBudgetProgram.setComputeUnitLimit({units: unitLimit}),
			web3.ComputeBudgetProgram.setComputeUnitPrice({
				microLamports: unitPrice,
			})
		);

		const bhash = await solanaStore.client.getLatestBlockhash("confirmed");
		txn.feePayer = srcWallet;
		txn.recentBlockhash = bhash.blockhash;

		await signAndSendTransaction(
			txn,
			"burn",
			[],
			() => {
			},
			store,
			null,
			alertStore
		);
	}
}

function onToggleSelect(position: any) {
	const mint = position.mint;

	if (!selectedPositions.value[mint]) {
		Vue.set(selectedPositions.value, mint, position);
	} else {
		Vue.delete(selectedPositions.value, mint);
	}
}

function shouldTrash(position: any) {
	const price = getTokenPrice(position.mint);
	if (!price) {
		return false;
	}

	return (
		position.totalPrice < 0.001 &&
		position.totalPrice < (position.account?.lamports / Math.pow(10, 9)) * price
	);
}

function onPositions(ps: any) {
	positions.value = ps;

	ps.forEach(async (el: any, i: number) => {
		const mint = el.account.data.parsed.info.mint;
		positions.value[i].mint = mint;
		const p = positions.value[i];

		await cacheStore.cacheToken(mint);

		const pnl = await fetchTokenPNL({
			mint,
			wallet: walletStore.address.toString(),
		});

		Vue.set(pnls.value, mint, pnl);

		if (shouldTrash(p)) {
			Vue.set(selectedPositions.value, mint, p);
		} else {
			Vue.delete(selectedPositions.value, mint);
		}
	});
}

function getTokenPrice(mint: any) {
	return cacheStore.tokenPricesUSD[mint]?.priceUSD || 0;
}

async function getActiveOrders() {
	orders.value = null;
	orders.value = TraderOrdersDTO(
		await getTraderOrders({
			wallet: walletStore.address.toString(),
			status: 1,
		})
	);

	orders.value.forEach((item) => cacheStore.cacheToken(item.mint));
}

async function getRecentTrades() {
	recentTrades.value = null;
	recentTrades.value = TradesDTO(
		await fetchTraderTrades({
			wallet: walletStore.address.toString(),
			limit: 12,
		})).sort((a, b) => b.timestamp - a.timestamp);

}

async function getInactiveOrders() {
	inactiveOrders.value = null;
	inactiveOrders.value = TraderOrdersDTO(
		await getTraderOrders({
			wallet: walletStore.address.toString(),
			status: 2,
		})
	);
	inactiveOrders.value.forEach((item) => cacheStore.cacheToken(item.mint));
}

async function deleteOrder(id: string): Promise<void> {
	try {
		orders.value = null;
		inactiveOrders.value = null;
		await closeTradeOrder(walletStore.address.toString(), id);
		alertStore.success("Order closed successfully");
	} catch (e: any) {
		const err = e.toString().replace("Error: ", "");
		alertStore.error(
			"Failed to close order",
			err.substring(0, 1).toUpperCase() + err.substring(1)
		);
	} finally {
		getActiveOrders();
		getInactiveOrders();
	}
}

function seeToken(mint: string) {
	router.push(`/${mint}`);
}

// setup
onMounted(mounted);
onUnmounted(unmounted);
ChartJS.register(ArcElement, Tooltip);

watch(
	() => showInactiveOrders.value,
	() => {
		if (inactiveOrders.value !== null) return;

		getInactiveOrders();
	}
);

return { __sfc: true,getChartData, currentAmount, reclaimSOL, reclaimUSD, profitLoss, totalProfitLoss, avgEntry, pnlPct, getPositionsFiltered, getOrders, walletStore, cacheStore, solanaStore, settingsStore, alertStore, MAX_CHART_TOKENS, loading, loadingTable, solBalance, totalUSD, positions, recentTrades, selectedPositions, tokenPrices, showRugs, pnls, orders, inactiveOrders, showInactiveOrders, privacy, paused, workerTrade, mounted, unmounted, initWorker, processMessages, getBalanceTokens, getTotalPrice, togglePrivacy, onReclaimTokens, onToggleSelect, shouldTrash, onPositions, getTokenPrice, getActiveOrders, getRecentTrades, getInactiveOrders, deleteOrder, seeToken, AccountAddress, TableResponsive, TokenImage, TokenPortfolio, SignatureLoader, convertToHumanAmount, convertToHumanNumber, ETradeOrderMethod, formatAmount, formatLargePrice, formatMedPrice, formatPrice, SOL_MINT, SOL_TOKEN, timeAgo, USDC_MINT, Doughnut, MultiTabs, ProfileIndexLoading, AmountEmoji }
}

})